import { NeedDto, NeedsActivityDto } from '@act/shared/data-transfer-objects';
import { Button, ControlledTextField, Masonry, Tile } from '@act/storybook';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import {
  Control,
  FormState,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  FormConfig,
  FormItem,
} from 'stories/forms/FormBuilder/FormBuilder.types';
import StyledAlert from 'toolkit/alerts/styled-alert';
import ActivityLearnMoreContent from './components/ActivityLearnMoreContent';
import { ActionFormValues } from './components/useNeedActionFormtypes';
import { convertTimeStringToMinutes } from './helpers/timeConverter';
import ResourceReferralForm from './resourcing/ResourceReferral.form';

interface UseAddActionFormConfigProps {
  overrideValues: Partial<ActionFormValues>;
  issue: { key: string; activityIds: number[] };
  activityOptions: NeedsActivityDto[];
  selectedActivity: NeedsActivityDto | null;
  setValue: UseFormSetValue<ActionFormValues>;
  getTimeWindowMessage: () => string;
  openLearnMoreDialog: (config: {
    title: string;
    content: React.ReactNode;
  }) => void;
  minDate: DateTime;
  control: Control<ActionFormValues>;
  watch: UseFormWatch<ActionFormValues>;
  durationFocused: boolean;
  setDurationFocused: (value: boolean) => void;
  formValues: ActionFormValues;
  resourceDetails: Array<{
    key: string;
    id?: number;
    resourceName?: string;
    contactName?: string;
    phoneNumber?: string;
    url?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    shouldDelete?: boolean;
  }>;
  formState: FormState<ActionFormValues>;
  expandedForm: string | null;
  expandResourceForm: (key: string) => void;
  confirmDelete: (index: number) => void;
  appendResourceDetails: () => void;
  referralsCountRef: React.MutableRefObject<number | null>;
  need: NeedDto;
}

export const useAddActionFormConfig = ({
  overrideValues,
  issue,
  activityOptions,
  selectedActivity,
  setValue,
  getTimeWindowMessage,
  openLearnMoreDialog,
  minDate,
  control,
  watch,
  durationFocused,
  setDurationFocused,
  formValues,
  resourceDetails,
  formState,
  expandedForm,
  expandResourceForm,
  confirmDelete,
  appendResourceDetails,
  referralsCountRef,
  need,
}: UseAddActionFormConfigProps): FormConfig<ActionFormValues> => {
  return useMemo(
    () =>
      [
        ...(overrideValues.activityId === undefined
          ? // Activity selection - custom for social isolation, standard select otherwise
            issue.key === 'issue_social-isolation'
            ? [
                {
                  type: 'passthrough',
                  content: (
                    <Box sx={{ margin: '22px 0 0' }}>
                      <Masonry>
                        {activityOptions.map(activity => (
                          <Tile
                            key={activity.id}
                            iconName={activity.iconName}
                            active={selectedActivity?.id === activity.id}
                            onClick={() => setValue('activityId', activity.id)}
                          >
                            <h3>{activity.label}</h3>
                            <p>{activity.overview}</p>
                          </Tile>
                        ))}
                      </Masonry>
                    </Box>
                  ),
                } as FormItem<ActionFormValues>,
              ]
            : [
                {
                  type: 'input',
                  name: 'activityId',
                  label: 'Activity*',
                  input: 'select',
                  props: {
                    required: true,
                    fullWidth: true,
                    options: activityOptions.map(activity => ({
                      label: activity.label,
                      value: activity.id,
                    })),
                  },
                } as FormItem<ActionFormValues>,
              ]
          : []),
        {
          type: 'passthrough',
          content: (
            <StyledAlert
              message={
                !selectedActivity ? (
                  getTimeWindowMessage()
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Box>{getTimeWindowMessage()}</Box>
                    <Box sx={{ minWidth: 'fit-content' }}>
                      <Typography
                        variant="labelLarge"
                        onClick={() =>
                          openLearnMoreDialog({
                            title: selectedActivity?.label,
                            content: (
                              <ActivityLearnMoreContent
                                activity={selectedActivity}
                              />
                            ),
                          })
                        }
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        Learn more
                      </Typography>
                    </Box>
                  </Box>
                )
              }
              severity="info"
              variant="filled"
            />
          ),
        } as FormItem<ActionFormValues>,
        ...(overrideValues.date === undefined
          ? [
              {
                type: 'input',
                name: 'date',
                label: 'Date*',
                input: 'date',
                props: {
                  required: true,
                  disablePast: false,
                  minDate: minDate,
                  inputType: 'date',
                },
              } as FormItem<ActionFormValues>,
            ]
          : []),
        {
          type: 'passthrough',
          content: (
            <ControlledTextField
              control={control}
              name="duration"
              label="Duration*"
              iconAdornment={
                watch('duration') && !durationFocused ? <>minutes</> : ''
              }
              onFocusCallback={() => {
                setDurationFocused(true);
              }}
              onBlurCallback={(
                e: React.FocusEvent<
                  HTMLInputElement | HTMLTextAreaElement,
                  Element
                >,
                fn
              ) => {
                const value = e.target.value;
                setDurationFocused(false);
                const minutes = convertTimeStringToMinutes(value);
                fn(minutes);
              }}
              required
            />
          ),
        } as FormItem<ActionFormValues>,
        ...(overrideValues.description === undefined
          ? [
              {
                type: 'input',
                name: 'description',
                label: 'Description*',
                input: 'text',
                props: {
                  required: true,
                  multiline: true,
                  minRows: 4,
                  maxRows: 4,
                  characterLimit: 500,
                },
              } as FormItem<ActionFormValues>,
            ]
          : []),
        {
          type: 'input',
          name: 'resourceReferred',
          label: 'Resource referred',
          input: 'checkbox',
          disabled: referralsCountRef.current > 0,
          props: {
            defaultChecked: false,
            onCheck: (value: boolean) => {
              if (resourceDetails.length === 0 && !!value) {
                appendResourceDetails();
              }
            },
          },
        } as FormItem<ActionFormValues>,
        // Resource referrals section - custom and conditional
        ...(formValues.resourceReferred
          ? [
              {
                type: 'passthrough',
                content: (
                  <Box sx={{ margin: '16px 0 24px' }}>
                    {resourceDetails?.map((field, index) => {
                      return (
                        <Box key={field?.key}>
                          <ResourceReferralForm
                            control={control}
                            details={resourceDetails[index]}
                            resourceDetailsIndex={index}
                            formState={formState}
                            expanded={expandedForm === field.key}
                            expandResourceForm={() =>
                              expandResourceForm(field.key)
                            }
                          />
                          <Box
                            sx={{
                              padding: '16px 0',
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Button
                              variant="contained"
                              onClick={appendResourceDetails}
                              startIcon={<AddIcon />}
                            >
                              Add another resource
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => confirmDelete(index)}
                              startIcon={<CloseIcon />}
                            >
                              Remove resource
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ),
              } as FormItem<ActionFormValues>,
            ]
          : []),
        // Mark as fulfilled checkbox
        ...(overrideValues.markAsFulfilled === undefined
          ? [
              {
                type: 'input',
                name: 'markAsFulfilled',
                label: 'Need fulfilled',
                input: 'checkbox',
                disabled: !!need?.fulfilledAt,
                props: {
                  defaultChecked: !!need?.fulfilledAt || false,
                },
              },
            ]
          : []),
      ] as FormConfig<ActionFormValues>,
    [
      overrideValues,
      issue,
      activityOptions,
      selectedActivity,
      setValue,
      getTimeWindowMessage,
      openLearnMoreDialog,
      minDate,
      control,
      watch,
      durationFocused,
      setDurationFocused,
      formValues.resourceReferred,
      resourceDetails,
      formState,
      expandedForm,
      expandResourceForm,
      confirmDelete,
      appendResourceDetails,
      referralsCountRef,
      need?.fulfilledAt,
    ]
  );
};
